import { useState } from 'react'
import { BrowserRouter, NavLink, Route, Routes, useLocation } from 'react-router-dom'
import { NAV } from './components/nav'
import { DisclaimerGate } from './components/DisclaimerGate'
import { useStore } from './store/store'
import { bankrollNow } from './store/selectors'
import { fmtCurrency } from './lib/format'
import { Pill } from './components/ui'
import { Dashboard } from './pages/Dashboard'
import { StockAnalyzer } from './pages/StockAnalyzer'
import { PredictionMarkets } from './pages/PredictionMarkets'
import { BettingOdds } from './pages/BettingOdds'
import { ModelLab } from './pages/ModelLab'
import { Backtest } from './pages/Backtest'
import { StrategyEvaluation } from './pages/StrategyEvaluation'
import { StrategyLab } from './pages/StrategyLab'
import { Calibration } from './pages/Calibration'
import { OddsConverter } from './pages/OddsConverter'
import { EvCalculator } from './pages/EvCalculator'
import { PositionSizing } from './pages/PositionSizing'
import { RiskManager } from './pages/RiskManager'
import { Journal } from './pages/Journal'
import { DataImport } from './pages/DataImport'
import { Watchlist } from './pages/Watchlist'
import { OpportunityScanner } from './pages/OpportunityScanner'
import { LegalSafety } from './pages/LegalSafety'
import { Settings } from './pages/Settings'

function Sidebar({ open, onNavigate }: { open: boolean; onNavigate: () => void }) {
  return (
    <aside className={`sidebar ${open ? 'open' : ''}`}>
      <div className="brand">
        <img src="/edgelab.svg" className="brand-logo" alt="" />
        <div>
          <div className="brand-name">EdgeLab</div>
          <div className="brand-sub">Edge, not luck</div>
        </div>
      </div>
      {NAV.map((group) => (
        <div key={group.label}>
          <div className="nav-group-label">{group.label}</div>
          {group.items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onNavigate}>
              <span className="ico">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
      <div style={{ flex: 1 }} />
      <div className="help" style={{ padding: '10px' }}>
        Local-first. Your data stays in this browser.
      </div>
    </aside>
  )
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const location = useLocation()
  const paperFirst = useStore((s) => s.meta.paperFirstMode)
  const setPaperFirst = useStore((s) => s.setPaperFirst)
  const bankroll = useStore(bankrollNow)
  const currency = useStore((s) => s.risk.baseCurrency)
  const title = NAV.flatMap((g) => g.items).find((i) => i.to === location.pathname)?.label ?? 'EdgeLab'

  return (
    <div className="topbar">
      <div className="row" style={{ alignItems: 'center', gap: 10 }}>
        <button className="menu-btn" onClick={onMenu} aria-label="Menu">
          {'\u2630'}
        </button>
        <span className="topbar-title">{title}</span>
      </div>
      <div className="topbar-right">
        <Pill tone="green">{fmtCurrency(bankroll, currency)}</Pill>
        <span onClick={() => setPaperFirst(!paperFirst)} style={{ cursor: 'pointer' }} title="Toggle paper-first mode">
          <Pill tone={paperFirst ? 'blue' : 'gray'}>{paperFirst ? '\uD83D\uDCC4 Paper-first ON' : 'Paper-first OFF'}</Pill>
        </span>
      </div>
    </div>
  )
}

function Layout() {
  const [open, setOpen] = useState(false)
  return (
    <div className="app">
      <div className={`backdrop ${open ? 'show' : ''}`} onClick={() => setOpen(false)} />
      <Sidebar open={open} onNavigate={() => setOpen(false)} />
      <div className="main">
        <TopBar onMenu={() => setOpen(true)} />
        <div className="content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/scanner" element={<OpportunityScanner />} />
            <Route path="/watchlist" element={<Watchlist />} />
            <Route path="/stocks" element={<StockAnalyzer />} />
            <Route path="/markets" element={<PredictionMarkets />} />
            <Route path="/odds" element={<BettingOdds />} />
            <Route path="/model" element={<ModelLab />} />
            <Route path="/backtest" element={<Backtest />} />
            <Route path="/evaluation" element={<StrategyEvaluation />} />
            <Route path="/strategies" element={<StrategyLab />} />
            <Route path="/calibration" element={<Calibration />} />
            <Route path="/tools/odds" element={<OddsConverter />} />
            <Route path="/tools/ev" element={<EvCalculator />} />
            <Route path="/tools/sizing" element={<PositionSizing />} />
            <Route path="/risk" element={<RiskManager />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/import" element={<DataImport />} />
            <Route path="/legal" element={<LegalSafety />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <DisclaimerGate />
      <Layout />
    </BrowserRouter>
  )
}
